import { Link } from 'react-router-dom';
import FilterPopover from '../../../../components/v2/FilterPopover.jsx';
import RowActionsMenu from '../../../../components/v2/RowActionsMenu.jsx';
import { ariaSort } from '../../../../components/v2/SortableTh.jsx';

// ---------------------------------------------------------------------------
// Column definitions — 2 columns
// ---------------------------------------------------------------------------
export const CORP_COLUMN_DEFS = [
  { key: 'corporate_name', label: 'Corporation', sortable: true, filterable: true },
  { key: 'campus_name', label: 'Campuses', sortable: true, filterable: true },
];

/**
 * CorporationsTable — data table for cross-corporation list with
 * sorting, filtering, sticky header, row selection, and impersonate action.
 */
const CorporationsTable = ({
  data,
  allData,
  visibleColumns,
  sortColumn,
  sortOrder,
  onSort,
  filters,
  onFilterChange,
  selectedIds,
  onSelectRow,
  onSelectAll,
  onImpersonate,
}) => {
  const activeColumns = CORP_COLUMN_DEFS.filter((col) =>
    visibleColumns.includes(col.key)
  );

  /** Collect unique filter values from the full (unfiltered) dataset */
  const getFilterValues = (columnKey) => {
    const values = new Set();
    (allData || data).forEach((row) => {
      const val = row[columnKey];
      values.add(val != null && val !== '' ? String(val) : '(empty)');
    });
    return [...values].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
    );
  };

  const allSelected =
    data.length > 0 && data.every((r) => selectedIds.includes(r.corporate_id));
  const someSelected = selectedIds.length > 0 && !allSelected;

  const renderSortIcon = (columnKey) => {
    if (sortColumn !== columnKey) {
      return (
        <svg
          className="dp-icon-sm dp-sort-icon dp-sort-icon--neutral"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"
          />
        </svg>
      );
    }
    return sortOrder === 'asc' ? (
      <svg
        className="dp-icon-sm dp-sort-icon dp-sort-icon--active"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M4.5 15.75l7.5-7.5 7.5 7.5"
        />
      </svg>
    ) : (
      <svg
        className="dp-icon-sm dp-sort-icon dp-sort-icon--active"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 8.25l-7.5 7.5-7.5-7.5"
        />
      </svg>
    );
  };

  /** Renders a single cell value */
  const renderCell = (row, columnKey) => {
    switch (columnKey) {
      case 'corporate_name':
        return row.corporate_name && row.corporate_id ? (
          <Link
            to={'/profiles/corporate/' + row.corporate_id}
            className="dp-device-link"
            style={{ fontWeight: 'var(--font-weight-semibold)' }}
          >
            {row.corporate_name}
          </Link>
        ) : (
          <span className="dp-cell-text" style={{ fontWeight: 'var(--font-weight-semibold)' }}>
            {row.corporate_name || '\u2014'}
          </span>
        );

      case 'campus_name':
        return row.campus_name ? (
          <span className="dp-cell-text crp-campuses-cell" title={row.campus_name}>
            {row.campus_name}
          </span>
        ) : (
          <span className="dp-cell-text--muted">&mdash;</span>
        );

      default:
        return (
          <span className="dp-cell-text">{row[columnKey] || '\u2014'}</span>
        );
    }
  };

  return (
    <div className="dp-table-wrap">
      <table className="dp-table">
        <thead className="dp-thead">
          <tr>
            {/* Checkbox column */}
            <th scope="col" className="dp-th dp-th--checkbox">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someSelected;
                }}
                onChange={onSelectAll}
                className="dp-checkbox"
                aria-label="Select all corporations"
              />
            </th>

            {/* Actions column */}
            <th scope="col" className="dp-th dp-th--actions">Edit</th>

            {/* Data columns */}
            {activeColumns.map((col) => (
              <th key={col.key} scope="col" className="dp-th" aria-sort={col.sortable ? ariaSort(sortColumn, sortOrder, col.key) : undefined}>
                <div className="dp-th__content">
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort(col.key)}
                      className="dp-th__sort-btn"
                      aria-label={`Sort by ${col.label}`}
                    >
                      {col.label}
                      {renderSortIcon(col.key)}
                    </button>
                  ) : (
                    <span>{col.label}</span>
                  )}

                  {col.filterable ? (
                    <FilterPopover
                      columnKey={col.filterKey || col.key}
                      label={col.label}
                      values={getFilterValues(col.filterKey || col.key)}
                      selectedValues={filters[col.filterKey || col.key] || []}
                      onChange={onFilterChange}
                      data={allData}
                    />
                  ) : null}
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="dp-tbody">
          {data.map((row) => {
            const isSelected = selectedIds.includes(row.corporate_id);
            const hasCampuses = !!row.campus_name;
            return (
              <tr
                key={row.corporate_id}
                className={`dp-tr ${isSelected ? 'is-selected' : ''}`}
              >
                {/* Checkbox */}
                <td className="dp-td dp-td--checkbox">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onSelectRow(row.corporate_id)}
                    className="dp-checkbox"
                    aria-label={`Select ${row.corporate_name}`}
                  />
                </td>

                {/* Row actions kebab — only if corp has campuses */}
                <td className="dp-td dp-td--actions">
                  {hasCampuses ? (
                    <CorpRowActionsMenu
                      corporation={row}
                      onImpersonate={onImpersonate}
                    />
                  ) : null}
                </td>

                {/* Data cells */}
                {activeColumns.map((col) => (
                  <td key={col.key} className="dp-td">
                    {renderCell(row, col.key)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/**
 * CorpRowActionsMenu — kebab menu with Impersonate action.
 */
const CorpRowActionsMenu = ({ corporation, onImpersonate }) => {
  return (
    <RowActionsMenu label={`Actions for ${corporation.corporate_name}`} classPrefix="dp">
      {(close) => (
        <>
          <button
            type="button"
            onClick={() => {
              onImpersonate(corporation);
              close();
            }}
            className="dp-row-actions__item"
          >
            <svg
              className="dp-icon-md dp-row-actions__item-icon"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            Impersonate
          </button>
        </>
      )}
    </RowActionsMenu>
  );
};

export default CorporationsTable;
